import {
  createChart,
  CandlestickSeries,
  createSeriesMarkers,
  ColorType,
  CrosshairMode,
  LineStyle,
  type IChartApi,
  type ISeriesApi,
  type ISeriesMarkersPluginApi,
  type IPriceLine,
  type SeriesMarker,
  type Time,
  type UTCTimestamp,
  type CandlestickData,
  type LogicalRange,
  type Coordinate,
} from 'lightweight-charts'
import {
  DrawingManager,
  getToolRegistry,
  TrendLine,
  Ray,
  ExtendedLine,
  HorizontalLine,
  HorizontalRay,
  ParallelChannel,
  FibRetracement,
  Rectangle,
  LongPosition,
  ShortPosition,
  Brush,
  TextAnnotation,
  type SerializedDrawing,
  type IDrawing,
  type Anchor,
  type DrawingStyle,
  type DrawingOptions,
  type DrawingCategory,
} from 'lightweight-charts-drawing'
import type { Bar } from '../core/types'

// The library ships the tool CLASSES but does not auto-register them in the
// shared ToolRegistry, which both interactive placement (setActiveTool → click)
// and import() rely on. Register the curated set once.
type ToolCtor = new (
  id: string,
  anchors?: Anchor[],
  style?: Partial<DrawingStyle>,
  options?: Partial<DrawingOptions>,
) => IDrawing
const BUILTIN_TOOLS: Array<[string, DrawingCategory, number, ToolCtor]> = [
  ['trend-line', 'line', 2, TrendLine],
  ['ray', 'line', 2, Ray],
  ['extended-line', 'line', 2, ExtendedLine],
  ['horizontal-line', 'line', 1, HorizontalLine],
  ['horizontal-ray', 'line', 1, HorizontalRay],
  ['parallel-channel', 'channel', 3, ParallelChannel],
  ['fib-retracement', 'fibonacci', 2, FibRetracement],
  ['rectangle', 'shape', 2, Rectangle],
  ['long-position', 'trading', 3, LongPosition],
  ['short-position', 'trading', 3, ShortPosition],
  ['brush', 'annotation', 2, Brush],
  ['text-annotation', 'annotation', 1, TextAnnotation],
]
function registerTools(): void {
  const reg = getToolRegistry()
  for (const [type, category, requiredAnchors, Ctor] of BUILTIN_TOOLS) {
    if (reg.has(type)) continue
    reg.register({
      type,
      name: type,
      category,
      requiredAnchors,
      factory: (id, anchors, style, options) => new Ctor(id, anchors, style, options),
    })
  }
}

export interface TradeMarker {
  time: number
  position: 'aboveBar' | 'belowBar' | 'inBar'
  color: string
  shape: 'arrowUp' | 'arrowDown' | 'circle' | 'square'
  text?: string
}

export interface PriceLineOpts {
  price: number
  color: string
  title?: string
  dashed?: boolean
}

// The ONLY module that imports Lightweight Charts. A version bump or library
// swap touches this file alone.

export interface ChartView {
  /** Bulk-set the visible window (used on load / stepBack / seek). */
  setData(bars: readonly Bar[]): void
  /** Cheap incremental append for forward stepping. */
  appendBar(bar: Bar): void
  /** Pin the latest bar near the right edge if auto-follow is on. */
  followRight(): void
  setAutoFollow(on: boolean): void
  readonly autoFollow: boolean
  /** Fired when the user scrolls left into history (auto-follow turns off). */
  onUserScrollAway(cb: () => void): void
  addMarker(m: TradeMarker): void
  clearMarkers(): void
  addPriceLine(opts: PriceLineOpts): IPriceLine
  removePriceLine(line: IPriceLine): void
  clearPriceLines(): void
  /** Activate a drawing tool by type ('trend-line', 'horizontal-line', …); null = select/move. */
  setDrawTool(toolType: string | null): void
  /** Called when a placement completes and the tool auto-reverts to select. */
  onPlacementDone(cb: () => void): void
  /** Cancel an in-progress placement (e.g. on Escape) and revert to select. */
  cancelDrawing(): void
  /** Remove the currently selected drawing, if any. */
  deleteSelectedDrawing(): void
  /** Remove all drawings. */
  clearDrawings(): void
  /** Serialize all drawings for persistence (opaque JSON). */
  exportDrawings(): unknown[]
  /** Restore drawings from a serialized array. */
  importDrawings(data: unknown[]): void
  /** Called whenever the drawing set changes (add/remove/update/clear). */
  onDrawingsChanged(cb: () => void): void
  takeScreenshot(): HTMLCanvasElement
  resize(): void
  destroy(): void
}

const RIGHT_MARGIN = 6 // blank bars kept to the right of the latest candle

function toPoint(b: Bar): CandlestickData<UTCTimestamp> {
  return {
    time: b.time as UTCTimestamp,
    open: b.open,
    high: b.high,
    low: b.low,
    close: b.close,
  }
}

export function createChartView(container: HTMLElement): ChartView {
  const chart: IChartApi = createChart(container, {
    autoSize: true,
    layout: {
      background: { type: ColorType.Solid, color: '#0e1117' },
      textColor: '#c9d1d9',
      attributionLogo: true,
    },
    grid: {
      vertLines: { color: '#1b2230' },
      horzLines: { color: '#1b2230' },
    },
    crosshair: { mode: CrosshairMode.Normal },
    rightPriceScale: { borderColor: '#2a3344' },
    timeScale: {
      borderColor: '#2a3344',
      timeVisible: true,
      secondsVisible: false,
      rightOffset: RIGHT_MARGIN,
    },
  })

  const series: ISeriesApi<'Candlestick'> = chart.addSeries(CandlestickSeries, {
    upColor: '#26a69a',
    downColor: '#ef5350',
    borderUpColor: '#26a69a',
    borderDownColor: '#ef5350',
    wickUpColor: '#26a69a',
    wickDownColor: '#ef5350',
  })

  const markersPlugin: ISeriesMarkersPluginApi<Time> = createSeriesMarkers(series, [])
  let markers: SeriesMarker<Time>[] = []
  let priceLines: IPriceLine[] = []

  // ---- drawing tools ----
  // The library renders 68 tool types and provides selection + drag-editing of
  // anchors + persistence, but (v0.1.1) does NOT implement click-to-PLACE new
  // drawings. So we add placement here: collect N clicks (per the tool's anchor
  // count, snapped to bar time/price), then instantiate + addDrawing. The
  // library takes over for rendering, selecting, dragging.
  registerTools()
  const drawMgr = new DrawingManager()
  drawMgr.attach(chart, series, container)
  const toolReg = getToolRegistry()
  const TOOL_ANCHORS = new Map(BUILTIN_TOOLS.map(([type, , n]) => [type, n]))

  let placeTool: string | null = null
  let placeNeed = 0
  let placeAnchors: Anchor[] = []
  let drawSeq = 0
  let placementDoneCb: (() => void) | null = null
  let preview: IDrawing | null = null // rubber-band shown while placing

  function pointFromEvent(e: { clientX: number; clientY: number }): Anchor | null {
    const rect = container.getBoundingClientRect()
    const price = series.coordinateToPrice((e.clientY - rect.top) as Coordinate)
    const t = chart.timeScale().coordinateToTime((e.clientX - rect.left) as Coordinate)
    if (price === null || t === null) return null
    return { time: t as Time, price: price as number }
  }
  function clearPreview(): void {
    if (preview) {
      preview.detach()
      preview = null
    }
  }
  function endPlacement(): void {
    clearPreview()
    placeAnchors = []
    placeTool = null
    placeNeed = 0
    drawMgr.setActiveTool(null)
  }

  // Capture-phase click fires reliably per tap (LWC's subscribeClick can drop
  // rapid repeats). Convert pixel → bar time/price, collect anchors, commit.
  container.addEventListener(
    'click',
    (e) => {
      if (!placeTool) return
      if ((e.target as HTMLElement | null)?.closest?.('#drawbar')) return // ignore toolbar
      const a = pointFromEvent(e)
      if (!a) return
      placeAnchors.push(a)
      if (placeAnchors.length < placeNeed) return
      clearPreview()
      const d = toolReg.createDrawing(placeTool, `d${Date.now().toString(36)}${drawSeq++}`, placeAnchors)
      if (d) drawMgr.addDrawing(d)
      placeAnchors = []
      placeTool = null
      placeNeed = 0
      drawMgr.setActiveTool(null)
      placementDoneCb?.()
    },
    true,
  )
  // Live preview: once the first anchor is down, draw the shape to the cursor.
  container.addEventListener(
    'pointermove',
    (e) => {
      if (!placeTool || placeAnchors.length === 0) return
      if ((e.target as HTMLElement | null)?.closest?.('#drawbar')) return
      const a = pointFromEvent(e)
      if (!a) return
      const anchors = [...placeAnchors, a]
      if (anchors.length < 2) return
      if (!preview) {
        preview = toolReg.createDrawing(placeTool, 'preview', anchors, {
          lineColor: '#e0b341',
          lineWidth: 1,
          lineDash: [4, 4],
        })
        if (preview) preview.attach(series, chart, container)
      } else {
        preview.setAnchors(anchors)
        preview.requestUpdate()
      }
    },
    true,
  )

  let lastIndex = -1 // logical index of the latest bar
  let follow = true
  let scrollAwayCb: (() => void) | null = null

  function windowWidth(): number {
    const px = container.clientWidth || 360
    return Math.min(400, Math.max(40, Math.floor(px / 7)))
  }

  function applyRightWindow(): void {
    if (lastIndex < 0) return
    const to = lastIndex + RIGHT_MARGIN
    const from = to - windowWidth()
    chart.timeScale().setVisibleLogicalRange({ from, to })
  }

  chart.timeScale().subscribeVisibleLogicalRangeChange((range: LogicalRange | null) => {
    if (!range || lastIndex < 0) return
    // Programmatic right-pinning keeps `to` >= lastIndex. If the user dragged
    // into history, `to` falls below the latest bar — turn off auto-follow.
    if (follow && range.to < lastIndex - 1) {
      follow = false
      scrollAwayCb?.()
    }
  })

  return {
    setData(bars) {
      series.setData(bars.map(toPoint))
      lastIndex = bars.length - 1
      if (follow) applyRightWindow()
    },
    appendBar(bar) {
      series.update(toPoint(bar))
      lastIndex++
      if (follow) applyRightWindow()
    },
    followRight() {
      follow = true
      applyRightWindow()
    },
    setAutoFollow(on) {
      follow = on
      if (on) applyRightWindow()
    },
    get autoFollow() {
      return follow
    },
    onUserScrollAway(cb) {
      scrollAwayCb = cb
    },
    addMarker(m) {
      markers.push({ ...m, time: m.time as UTCTimestamp })
      markers.sort((a, b) => (a.time as number) - (b.time as number))
      markersPlugin.setMarkers(markers)
    },
    clearMarkers() {
      markers = []
      markersPlugin.setMarkers(markers)
    },
    addPriceLine(opts) {
      const line = series.createPriceLine({
        price: opts.price,
        color: opts.color,
        lineWidth: 1,
        lineStyle: opts.dashed ? LineStyle.Dashed : LineStyle.Solid,
        axisLabelVisible: true,
        title: opts.title ?? '',
      })
      priceLines.push(line)
      return line
    },
    removePriceLine(line) {
      series.removePriceLine(line)
      priceLines = priceLines.filter((l) => l !== line)
    },
    clearPriceLines() {
      for (const l of priceLines) series.removePriceLine(l)
      priceLines = []
    },
    setDrawTool(toolType) {
      clearPreview()
      placeTool = toolType
      placeAnchors = []
      placeNeed = toolType ? (TOOL_ANCHORS.get(toolType) ?? 2) : 0
      drawMgr.setActiveTool(toolType)
    },
    onPlacementDone(cb) {
      placementDoneCb = cb
    },
    cancelDrawing() {
      endPlacement()
    },
    deleteSelectedDrawing() {
      const sel = drawMgr.getSelectedDrawing()
      if (sel) drawMgr.removeDrawing(sel.id)
    },
    clearDrawings() {
      drawMgr.clearAll()
    },
    exportDrawings() {
      return drawMgr.exportDrawings()
    },
    importDrawings(data) {
      drawMgr.importDrawings(data as SerializedDrawing[], (type, d) =>
        toolReg.createDrawing(type, d.id, d.anchors, d.style, d.options),
      )
    },
    onDrawingsChanged(cb) {
      const events = ['drawing:added', 'drawing:removed', 'drawing:updated', 'drawing:cleared'] as const
      for (const ev of events) drawMgr.on(ev, () => cb())
    },
    takeScreenshot() {
      return chart.takeScreenshot()
    },
    resize() {
      // autoSize handles most cases; expose for explicit re-layout.
      if (follow) applyRightWindow()
    },
    destroy() {
      chart.remove()
    },
  }
}
